import { useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { C } from '../../constants/colors.js';
import { F, N, L } from '../../constants/fonts.js';
import { LEVELS } from '../../constants/levels.js';
import { SIGHT_SENT } from '../../constants/content.js';
import { Btn } from '../ui/Btn.jsx';
import { PB } from '../ui/ProgressBar.jsx';
import { LB } from '../ui/LevelBadge.jsx';
import { FinleyInsightBanner } from '../ui/FinleyInsightBanner.jsx';
import { LoadingDots } from '../ui/LoadingDots.jsx';
import { getInsightMessage } from '../../config/insightMessages.js';
import { getSightWords } from '../../lib/content-fetch.js';
import { getSpellingWords } from '../../lib/spelling.js';
import { getDueWords, prioritizeWithLeitner } from '../../lib/leitner.js';
import { rqConfettiBurst } from '../../lib/animations.js';
import { sightFontSize } from '../../lib/utils.js';
import { useTTS } from '../../hooks/useTTS.js';
import { popIn, slideUp, tapBounce } from '../../lib/motionPresets.js';

export function SightGame({tracker, profile, onComplete, onRecord, online, sessionActivityIndex = 0, isPlaylistSession = false}) {
  const [words, setWords] = useState(null);
  const [idx, setIdx] = useState(0);
  const [phase, setPhase] = useState("show");
  const [cor, setCor] = useState(0);
  const [done, setDone] = useState(false);
  const [insightMessage, setInsightMessage] = useState('');
  const [insightVisible, setInsightVisible] = useState(false);
  const [dueSet, setDueSet] = useState(new Set());
  const wStart = useRef(Date.now());
  const [isFlipped, setIsFlipped] = useState(false);
  const [glowKey, setGlowKey] = useState(0);
  const [btnReady, setBtnReady] = useState(false);
  const [hasFlipped, setHasFlipped] = useState(false);
  const prevBktRef = useRef(tracker.bkt || {});
  const { speak, stop } = useTTS();
  const lv = tracker.currentLevel;
  const ml = LEVELS[lv]?.ml?.s || "Sight Words";
  const age = profile.age || 7;
  const chillMsg = tracker.chillMode ? "You're doing great! Let's review together 🌟" : null;
  const sightCount = tracker.chillMode ? 4 : 8;

  useEffect(() => {
    getSightWords(lv, profile, online).then(raw => {
      const items = raw.map(w => typeof w === "string" ? {word: w} : w);
      const seen = new Set();
      const unique = items.filter(i => { if (seen.has(i.word)) return false; seen.add(i.word); return true; });
      const spArr = getSpellingWords(profile?.id).map(sw => ({word:sw.word,sentence:sw.sentence,phonemes:sw.phonemes,isSpelling:true}));
      const existSet = new Set(unique.map(i => i.word));
      const newSp = spArr.filter(s => !existSet.has(s.word)).slice(0, 3);
      const combined = [...newSp, ...unique];
      const prioritized = prioritizeWithLeitner(combined, tracker.wordMastery, profile.age);
      const dueWords = getDueWords(tracker.wordMastery, profile.age || 7, 20);
      setDueSet(new Set(dueWords));
      setWords(prioritized.slice(0, sightCount + newSp.length));
    });
  }, []);

  useEffect(() => {
    if (!words || !words[idx]) return;
    const currentWord = words[idx]?.word;
    const triggers = [];
    if (dueSet.has(currentWord)) triggers.push('leitner_review');
    if (idx > 0 && idx % 3 === 0) triggers.push('periodic');
    const msg = getInsightMessage({
      activityKey: `sight:${sessionActivityIndex}:${idx}:${currentWord}`,
      sessionStart: isPlaylistSession && sessionActivityIndex === 0 && idx === 0,
      triggers
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [words, idx, dueSet, sessionActivityIndex, isPlaylistSession]);

  useEffect(() => {
    const prev = prevBktRef.current || {};
    const next = tracker.bkt || {};
    const crossed = Object.keys(next).some((skill) => {
      const p = prev[skill] || 0;
      const n = next[skill] || 0;
      return (p < 0.6 && n >= 0.6) || (p < 0.85 && n >= 0.85);
    });
    prevBktRef.current = next;
    if (!crossed) return;
    const msg = getInsightMessage({
      activityKey: `sight:milestone:${sessionActivityIndex}:${idx}`,
      triggers: ['skill_milestone']
    });
    if (msg) { setInsightMessage(msg); setInsightVisible(true); }
  }, [tracker.bkt, idx, sessionActivityIndex]);

  const cw = words ? words[idx] : null;
  const cwText = cw?.word || "";
  const hasInteracted = useRef(false);

  useEffect(() => { if (hasInteracted.current && cw && lv <= 4 && phase === "show") speak(cwText, { rate: 0.95, pitch: 1.0 }); }, [idx, phase, cwText, cw, lv, speak]);
  useEffect(() => () => stop(), [stop]);
  useEffect(() => {
    setIsFlipped(false); setHasFlipped(false); setGlowKey(k => k+1); setBtnReady(false); wStart.current = Date.now();
    const t = setTimeout(() => setBtnReady(true), 1200);
    return () => clearTimeout(t);
  }, [idx]);

  const mapped = SIGHT_SENT[cwText.toLowerCase()];
  const backSentence = cw?.sentence || (mapped ? mapped.s : null);
  const backEmoji = mapped ? mapped.e : null;
  const backDef = cw?.def || null;
  const knowLabel = age <= 6 ? "Yep!" : age <= 9 ? "Got It!" : "Got It";
  const learnLabel = age <= 6 ? "Try Again" : age <= 9 ? "Keep Trying" : "Review Again";
  const advance = () => { if (idx + 1 >= words.length) setDone(true); else { setIdx(i => i+1); setPhase("show"); } };
  const selfReport = (known) => {
    const ms = Date.now() - wStart.current;
    if (known) { setCor(x => x+1); rqConfettiBurst({count:20,spread:50}); }
    onRecord("sight", cwText, known, ms);
    advance();
  };

  if (!words) return <LoadingDots msg="Loading words..."/>;
  if (words.length === 0) return <div style={{textAlign:"center",padding:"36px 24px"}}><div style={{fontSize:48,marginBottom:10}}>📚</div><p style={{fontFamily:N,fontSize:15,color:C.textMid}}>No words available right now.</p><Btn onClick={() => onComplete(0)}>Go Back</Btn></div>;
  if (done) return <div style={{textAlign:"center",padding:"36px 24px"}}><div style={{fontSize:64,marginBottom:10,animation:"float 2s ease-in-out infinite"}}>🌟</div><h2 style={{fontFamily:F,fontSize:28,color:C.text}}>Great Job!</h2><p style={{fontFamily:N,fontSize:17,color:C.textMid,margin:"14px 0 20px"}}>{cor}/{words.length} words learned</p><Btn onClick={() => onComplete(cor)}>Keep Going →</Btn></div>;

  const cardFs = sightFontSize(cwText, lv >= 11 ? 36 : 48);
  const cardTextClass = cwText.length > 10 ? 'text-3xl' : cwText.length > 7 ? 'text-4xl' : 'text-5xl';
  const cardStyle = {overflowWrap:'anywhere', wordBreak:'break-word', padding:'12px'};

  return (
    <motion.div {...slideUp} className="relative text-center" style={{padding:"20px 24px"}}>
      <FinleyInsightBanner message={insightMessage} visible={insightVisible} />
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <span style={{fontFamily:N,fontSize:13,color:C.textLight}}>{idx+1}/{words.length}</span>
        <LB level={lv}/>
        <span style={{fontFamily:N,fontSize:13,color:C.primary,fontWeight:700}}>⭐ {cor}</span>
      </div>
      <PB current={idx} total={words.length}/>
      <p style={{fontFamily:F,fontSize:16,color:C.secondary,margin:"20px 0 4px"}}>{ml}</p>
      {chillMsg && <p style={{fontFamily:N,fontSize:13,color:C.success,margin:"0 0 8px"}}>{chillMsg}</p>}
      {phase === "show" && (
        <motion.div {...popIn} style={{margin:"16px 0"}}>
          {lv <= 4 ? <>
            <p style={{fontFamily:N,fontSize:14,color:C.textMid,margin:"0 0 14px"}}>Listen and learn this word:</p>
            <motion.div key={glowKey} {...tapBounce} className="mx-auto mb-4 flex h-[180px] w-[220px] cursor-pointer flex-col items-center justify-center rounded-finley-xl shadow-finley-lg" style={{background:'linear-gradient(135deg, #7209B7, #a855f7)',animation:'rqPulseGlow 2s ease-in-out infinite',...cardStyle}} onClick={() => { hasInteracted.current = true; speak(cwText, { rate: 0.95, pitch: 1.0 }); }}>
              <div className={`max-w-full text-center font-finley-display font-bold leading-tight text-white ${cardTextClass}`} style={{fontSize:Math.min(cardFs, cwText.length > 10 ? 34 : cardFs),...cardStyle}}>{cwText}</div>
              {backEmoji && <div style={{fontSize:36,marginTop:8}}>{backEmoji}</div>}
            </motion.div>
            <p style={{fontFamily:N,fontSize:12,color:C.textLight,margin:'0 0 16px'}}>Tap card to hear the word</p>
            <div style={{display:'flex',gap:10,justifyContent:'center',opacity:btnReady?1:0.4,transition:'opacity 0.3s'}}>
              <Btn onClick={btnReady ? () => selfReport(true) : undefined} disabled={!btnReady} color={C.success}>{knowLabel}</Btn>
              <Btn onClick={btnReady ? () => selfReport(false) : undefined} disabled={!btnReady} color={C.textMid}>{learnLabel}</Btn>
            </div>
          </> : lv <= 6 ? <>
            <p style={{fontFamily:N,fontSize:14,color:C.textMid,margin:"0 0 14px"}}>Study this word:</p>
            <motion.div {...tapBounce} onClick={() => { setIsFlipped(f => !f); setHasFlipped(true); }} style={{perspective:'800px',width:220,height:180,cursor:'pointer',margin:'0 auto 16px'}}>
              <div style={{width:'100%',height:'100%',position:'relative',transformStyle:'preserve-3d',transition:'transform 0.6s cubic-bezier(0.4,0,0.2,1)',transform:isFlipped?'rotateY(180deg)':'rotateY(0deg)'}}>
                <div className={`absolute inset-0 flex items-center justify-center rounded-[20px] text-center font-finley-display font-bold leading-tight text-white ${cardTextClass}`} style={{background:'linear-gradient(135deg, #7209B7, #a855f7)',backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',fontSize:Math.min(cardFs, cwText.length > 10 ? 34 : cardFs),boxShadow:'0 8px 30px rgba(114,9,183,0.35)',...cardStyle}}>{cwText}</div>
                <div style={{position:'absolute',inset:0,borderRadius:20,background:'linear-gradient(135deg, #FF6B35, #ff8a3d)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',color:'#fff',padding:20,textAlign:'center',transform:'rotateY(180deg)',boxShadow:'0 8px 30px rgba(255,107,53,0.35)',...cardStyle}}>
                  {backEmoji && <div style={{fontSize:32,marginBottom:6}}>{backEmoji}</div>}
                  {backSentence ? <div style={{fontFamily:L,fontSize:14,fontStyle:'italic',opacity:0.9,overflowWrap:'break-word'}}>"{backSentence}"</div> : <div style={{fontFamily:F,fontSize:Math.max(18,cardFs-6),...cardStyle}}>{cwText}</div>}
                </div>
              </div>
            </motion.div>
            {!hasFlipped && <p style={{fontFamily:N,fontSize:12,color:C.textLight,margin:'0 0 12px',textAlign:'center'}}>Tap to flip!</p>}
            {hasFlipped && <div style={{display:'flex',gap:10,justifyContent:'center',opacity:btnReady?1:0.4,transition:'opacity 0.3s'}}>
              <Btn onClick={btnReady ? () => selfReport(true) : undefined} disabled={!btnReady} color={C.success}>{knowLabel}</Btn>
              <Btn onClick={btnReady ? () => selfReport(false) : undefined} disabled={!btnReady} color={C.textMid}>{learnLabel}</Btn>
            </div>}
          </> : <>
            <p style={{fontFamily:N,fontSize:14,color:C.textMid,margin:"0 0 14px"}}>Study this word:</p>
            <motion.div {...tapBounce} onClick={() => { setIsFlipped(f => !f); setHasFlipped(true); }} style={{perspective:'800px',width:220,height:180,cursor:'pointer',margin:'0 auto 16px'}}>
              <div style={{width:'100%',height:'100%',position:'relative',transformStyle:'preserve-3d',transition:'transform 0.6s cubic-bezier(0.4,0,0.2,1)',transform:isFlipped?'rotateY(180deg)':'rotateY(0deg)'}}>
                <div className={`absolute inset-0 flex items-center justify-center rounded-[20px] text-center font-finley-display font-bold leading-tight text-white ${cardTextClass}`} style={{background:'linear-gradient(135deg, #7209B7, #a855f7)',backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',fontSize:Math.min(cardFs, cwText.length > 10 ? 34 : cardFs),boxShadow:'0 8px 30px rgba(114,9,183,0.35)',...cardStyle}}>{cwText}</div>
                <div style={{position:'absolute',inset:0,borderRadius:20,background:'linear-gradient(135deg, #FF6B35, #ff8a3d)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',backfaceVisibility:'hidden',WebkitBackfaceVisibility:'hidden',color:'#fff',padding:20,textAlign:'center',transform:'rotateY(180deg)',boxShadow:'0 8px 30px rgba(255,107,53,0.35)',...cardStyle}}>
                  {backDef && <div style={{fontFamily:N,fontSize:16,marginBottom:8,overflowWrap:'break-word'}}>{backDef}</div>}
                  {backSentence ? <div style={{fontFamily:L,fontSize:14,fontStyle:'italic',opacity:0.9,overflowWrap:'break-word'}}>"{backSentence}"</div> : !backDef && <div style={{fontFamily:F,fontSize:Math.max(18,cardFs-6),...cardStyle}}>{cwText}</div>}
                </div>
              </div>
            </motion.div>
            {!hasFlipped && <p style={{fontFamily:N,fontSize:12,color:C.textLight,margin:'0 0 12px',textAlign:'center'}}>Tap to flip!</p>}
            {hasFlipped && <div style={{display:'flex',gap:10,justifyContent:'center',opacity:btnReady?1:0.4,transition:'opacity 0.3s'}}>
              <Btn onClick={btnReady ? () => selfReport(true) : undefined} disabled={!btnReady} color={C.success}>{knowLabel}</Btn>
              <Btn onClick={btnReady ? () => selfReport(false) : undefined} disabled={!btnReady} color={C.textMid}>{learnLabel}</Btn>
            </div>}
          </>}
        </motion.div>
      )}
    </motion.div>
  );
}
